import { generatePath } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { StatusColor } from '../../../common/enums.js';
import {
  approverContactMsg,
  cancelledMsg,
  deliveredMsg,
  openMsg,
  pendingApprovalMsg,
  processingMsg,
  redeemRequestDisplayIdMsg,
  t,
} from '../../../common/i18n/index.js';
import { formatTimestampToDDMMYYYY } from '../../../common/utils/dateUtils.js';
import { SubscriptionActionHeader } from '../../../generated/api/subscriptionActionHeader.js';
import { SearchResultRow } from '../SearchResultRow.js';
import { HeaderSectionContent, SectionContent } from '../SectionContent.js';
import { getResultObjectWithHighlightedValues } from '../highlightFunctions.js';
import type { SearchResultBaseProps } from '../SearchResultRow.js';

export interface RedeemRequestProps extends SearchResultBaseProps {
  redeemRequest: SubscriptionActionHeader;
}

export interface SubscriptionActionStatus {
  color: StatusColor;
  text: string;
}

export const subscriptionActionStatus = (status: SubscriptionActionHeader.StatusEnum): SubscriptionActionStatus => {
  switch (status) {
    case SubscriptionActionHeader.StatusEnum.PENDING:
      return { color: StatusColor.GREY, text: t.TJ54(openMsg) };
    case SubscriptionActionHeader.StatusEnum.PENDING_APPROVAL:
      return { color: StatusColor.SALMON, text: t.ZXA6(pendingApprovalMsg) };
    case SubscriptionActionHeader.StatusEnum.COMPLETED:
      return { color: StatusColor.GREEN, text: t.PQGG(deliveredMsg) };
    case SubscriptionActionHeader.StatusEnum.CANCELLED:
      return { color: StatusColor.GREY, text: t.PEKP(cancelledMsg) };
    case SubscriptionActionHeader.StatusEnum.ERROR:
    default:
      return { color: StatusColor.YELLOW, text: t.VZZW(processingMsg) };
  }
};

export const RedeemRequest = ({ redeemRequest, matchedFields, searchTerms, ...rest }: RedeemRequestProps) => {
  const {
    subscriptionActionDisplayId,
    subscriptionName,
    subscriptionContactName,
    subscriptionSerialNumber,
    requesterContactName,
    subscriptionDisplayId,
  } = getResultObjectWithHighlightedValues(redeemRequest, matchedFields, searchTerms);
  const { color, text } = subscriptionActionStatus(redeemRequest.status);
  return (
    <SearchResultRow
      icon="invoice"
      upperSection={
        <HeaderSectionContent
          left={
            <>
              {t.B951('Redeem request')}: {subscriptionDisplayId}
            </>
          }
          right={subscriptionContactName}
        />
      }
      lowerSection={
        <SectionContent
          statusColor={color}
          left={`${text} ${formatTimestampToDDMMYYYY(redeemRequest.lastModified) || ''}`}
          right={subscriptionName}
        />
      }
      additionalHighlightableFields={[
        { title: 'IMEI', value: subscriptionSerialNumber, key: 'subscriptionSerialNumber' },
        { title: t.GOZ7(approverContactMsg), value: requesterContactName, key: 'requesterContactName' },
        {
          title: t.K84B(redeemRequestDisplayIdMsg),
          value: subscriptionActionDisplayId,
          key: 'subscriptionActionDisplayId',
        },
      ]}
      matchedFields={matchedFields}
      linkHref={`${generatePath(paths.CUSTOMER_ORDER_REDEEM_REQUEST, {
        requestId: redeemRequest.subscriptionActionDisplayId,
      })}?mdmId=${redeemRequest.accountMasterId}`}
      {...rest}
    />
  );
};
